import React, { useState, useEffect } from 'react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';
import { 
  Heart, Star, Users, TrendingUp, Clock, X, RefreshCw, Search,
  UserPlus, MessageSquare, Briefcase, Award, DollarSign, Calendar,
  ChevronRight, Crown, Zap, BarChart3, PieChart
} from 'lucide-react';
import RealTimeChat from '../RealTimeChat';

export default function RelationshipManagement({ onClose, onHireWorker }) {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('preferred');
  const [preferredWorkers, setPreferredWorkers] = useState([]);
  const [recentWorkers, setRecentWorkers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showMessaging, setShowMessaging] = useState(false);
  const [messagingRecipient, setMessagingRecipient] = useState(null);

  useEffect(() => {
    loadRelationshipData();
  }, []);

  const loadRelationshipData = async () => {
    setLoading(true);
    try {
      const [preferredRes, recentRes, analyticsRes] = await Promise.all([
        apiCall('/api/employer/preferred-workers'),
        apiCall('/api/employer/recent-workers'),
        apiCall('/api/employer/workforce-analytics')
      ]);
      
      setPreferredWorkers(preferredRes.workers || []);
      setRecentWorkers(recentRes.workers || []);
      setAnalytics(analyticsRes);
    } catch (error) {
      console.error('Failed to load relationship data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePreferred = async (workerId) => {
    if (!window.confirm('Remove this worker from your preferred list?')) return;

    try {
      await apiCall(`/api/employer/preferred-workers/${workerId}`, {
        method: 'DELETE'
      });
      toast.success('Worker removed from preferred list');
      loadRelationshipData();
    } catch (error) {
      toast.error('Failed to remove worker');
    }
  };

  const handleQuickHire = async (workerId) => {
    onHireWorker?.(workerId);
  };

  const handleStartChat = (worker) => {
    setMessagingRecipient({
      id: worker._id,
      name: worker.full_name,
      role: 'worker'
    });
    setShowMessaging(true);
  };

  const filteredPreferred = preferredWorkers.filter(worker => 
    searchQuery ? worker.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) : true
  );

  const filteredRecent = recentWorkers.filter(worker =>
    searchQuery ? worker.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) : true
  );

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-pink-600 via-rose-600 to-red-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                <Heart className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Workforce Relationships</h2>
                <p className="text-white/80">Manage your preferred workers and hiring history</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-xl transition">
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-4 gap-4 mt-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="flex items-center gap-2 text-white/70 mb-1">
                <Heart className="w-4 h-4" />
                <span className="text-sm">Preferred</span>
              </div>
              <p className="text-2xl font-bold">{preferredWorkers.length}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="flex items-center gap-2 text-white/70 mb-1">
                <Users className="w-4 h-4" />
                <span className="text-sm">Total Hired</span>
              </div>
              <p className="text-2xl font-bold">{analytics?.total_workers_hired || 0}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="flex items-center gap-2 text-white/70 mb-1">
                <TrendingUp className="w-4 h-4" />
                <span className="text-sm">Repeat Hires</span>
              </div>
              <p className="text-2xl font-bold">{analytics?.repeat_hire_rate || 0}%</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="flex items-center gap-2 text-white/70 mb-1">
                <DollarSign className="w-4 h-4" />
                <span className="text-sm">Total Spent</span>
              </div>
              <p className="text-2xl font-bold">${analytics?.total_spent?.toFixed(0) || 0}</p>
            </div>
          </div>
        </div>

        {/* Tabs & Search */}
        <div className="border-b border-gray-200 p-4 bg-gray-50">
          <div className="flex items-center justify-between gap-4">
            <div className="flex gap-1">
              {[
                { id: 'preferred', label: 'Preferred Workers', icon: <Heart className="w-4 h-4" />, count: preferredWorkers.length },
                { id: 'recent', label: 'Recent Workers', icon: <Clock className="w-4 h-4" />, count: recentWorkers.length },
                { id: 'analytics', label: 'Analytics', icon: <BarChart3 className="w-4 h-4" /> }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition ${
                    activeTab === tab.id
                      ? 'bg-pink-100 text-pink-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                  {tab.count !== undefined && (
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      activeTab === tab.id ? 'bg-pink-200' : 'bg-gray-200'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {activeTab !== 'analytics' && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search workers..."
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 w-64"
                />
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[55vh]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-pink-600 animate-spin" />
            </div>
          ) : (
            <>
              {/* Preferred Workers Tab */}
              {activeTab === 'preferred' && (
                <div>
                  {filteredPreferred.length === 0 ? (
                    <div className="text-center py-12">
                      <Heart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">No Preferred Workers</h3>
                      <p className="text-gray-500">Add workers to your preferred list for quick rehiring.</p>
                    </div>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-4">
                      {filteredPreferred.map((worker) => (
                        <div 
                          key={worker._id}
                          className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md hover:border-pink-200 transition"
                        >
                          <div className="flex items-start gap-4">
                            <div className="relative">
                              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center overflow-hidden">
                                {worker.profile_photo ? (
                                  <img src={worker.profile_photo} alt={worker.full_name} className="w-full h-full object-cover" />
                                ) : (
                                  <span className="text-2xl font-bold text-white">
                                    {worker.full_name?.charAt(0) || 'W'}
                                  </span>
                                )}
                              </div>
                              <div className="absolute -top-1 -right-1 p-1 bg-pink-500 rounded-full">
                                <Heart className="w-3 h-3 text-white fill-white" />
                              </div>
                            </div>

                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900">{worker.full_name}</h4>
                              <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                                  {worker.rating?.toFixed(1) || 'New'}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Briefcase className="w-4 h-4" />
                                  {worker.jobs_with_you || 0} jobs with you
                                </span>
                              </div>

                              {/* Skills */}
                              {worker.skills && worker.skills.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {worker.skills.slice(0, 3).map((skill, idx) => (
                                    <span key={idx} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                                      {skill}
                                    </span>
                                  ))}
                                </div>
                              )}

                              {/* Last Hired */}
                              {worker.last_hired && (
                                <p className="text-xs text-gray-400 mt-2">
                                  Last hired: {new Date(worker.last_hired).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex gap-2 mt-4 pt-4 border-t border-gray-100">
                            <button
                              onClick={() => handleQuickHire(worker._id)}
                              className="flex-1 px-3 py-2 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-lg font-medium hover:shadow-md transition flex items-center justify-center gap-2"
                            >
                              <Zap className="w-4 h-4" />
                              Quick Hire
                            </button>
                            <button 
                              onClick={() => handleStartChat(worker)}
                              className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-blue-50 hover:text-blue-600 transition"
                              data-testid="chat-worker-btn"
                              title="Chat with worker"
                            >
                              <MessageSquare className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleRemovePreferred(worker._id)}
                              className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-red-50 hover:text-red-600 transition"
                              title="Remove from favorites"
                            >
                              <Heart className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Recent Workers Tab */}
              {activeTab === 'recent' && (
                <div>
                  {filteredRecent.length === 0 ? (
                    <div className="text-center py-12">
                      <Clock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">No Recent Workers</h3>
                      <p className="text-gray-500">Workers you've hired recently will appear here.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {filteredRecent.map((worker) => (
                        <div 
                          key={worker._id}
                          className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center overflow-hidden">
                                {worker.profile_photo ? (
                                  <img src={worker.profile_photo} alt={worker.full_name} className="w-full h-full object-cover" />
                                ) : (
                                  <span className="text-xl font-bold text-white">
                                    {worker.full_name?.charAt(0) || 'W'}
                                  </span>
                                )}
                              </div>
                              <div>
                                <h4 className="font-semibold text-gray-900">{worker.full_name}</h4>
                                <div className="flex items-center gap-3 text-sm text-gray-500">
                                  <span className="flex items-center gap-1">
                                    <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                                    {worker.rating?.toFixed(1) || 'New'}
                                  </span>
                                  <span>Last job: {worker.last_job_title}</span>
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleQuickHire(worker._id)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition flex items-center gap-2"
                              >
                                <UserPlus className="w-4 h-4" />
                                Hire Again
                              </button>
                              {!worker.is_preferred && (
                                <button
                                  onClick={async () => {
                                    try {
                                      await apiCall('/api/employer/preferred-workers', {
                                        method: 'POST',
                                        body: JSON.stringify({ worker_id: worker._id })
                                      });
                                      toast.success('Added to preferred workers');
                                      loadRelationshipData();
                                    } catch (error) {
                                      toast.error('Failed to add to preferred');
                                    }
                                  }}
                                  className="p-2 bg-pink-100 text-pink-600 rounded-lg hover:bg-pink-200 transition"
                                >
                                  <Heart className="w-5 h-5" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Analytics Tab */}
              {activeTab === 'analytics' && (
                <div className="space-y-6">
                  {/* Top Performers */}
                  <div className="bg-white rounded-xl border border-gray-200 p-5">
                    <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Crown className="w-5 h-5 text-amber-500" />
                      Top Performing Workers
                    </h3>
                    {analytics?.top_performers?.length > 0 ? (
                      <div className="space-y-3">
                        {analytics.top_performers.map((worker, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                                idx === 0 ? 'bg-amber-100 text-amber-700' :
                                idx === 1 ? 'bg-gray-100 text-gray-700' :
                                idx === 2 ? 'bg-orange-100 text-orange-700' :
                                'bg-gray-50 text-gray-500'
                              }`}>
                                {idx + 1}
                              </span>
                              <div>
                                <p className="font-medium text-gray-900">{worker.full_name}</p>
                                <p className="text-xs text-gray-500">{worker.jobs_completed} jobs completed</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                              <span className="font-semibold">{worker.rating?.toFixed(1)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 bg-gray-50 rounded-lg">
                        <p className="text-gray-500">Not enough data yet</p>
                      </div>
                    )}
                  </div>

                  {/* Spending Analytics */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-white rounded-xl border border-gray-200 p-5">
                      <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-emerald-600" />
                        Spending by Category
                      </h3>
                      {analytics?.spending_by_category?.length > 0 ? (
                        <div className="space-y-3">
                          {analytics.spending_by_category.map((cat, idx) => (
                            <div key={idx} className="flex items-center justify-between">
                              <span className="text-sm text-gray-600">{cat.category}</span>
                              <span className="font-semibold text-gray-900">${cat.amount.toFixed(0)}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-6 bg-gray-50 rounded-lg">
                          <p className="text-gray-500">No spending data</p>
                        </div>
                      )}
                    </div>

                    <div className="bg-white rounded-xl border border-gray-200 p-5">
                      <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-blue-600" />
                        Hiring Trends
                      </h3>
                      {analytics?.monthly_hires?.length > 0 ? (
                        <div className="space-y-3">
                          {analytics.monthly_hires.map((month, idx) => (
                            <div key={idx} className="flex items-center justify-between">
                              <span className="text-sm text-gray-600">{month.month}</span>
                              <div className="flex items-center gap-2">
                                <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-blue-500 rounded-full"
                                    style={{ width: `${(month.count / Math.max(...analytics.monthly_hires.map(m => m.count))) * 100}%` }}
                                  />
                                </div>
                                <span className="text-sm font-medium text-gray-900 w-8">{month.count}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-6 bg-gray-50 rounded-lg">
                          <p className="text-gray-500">No hiring data</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Chat Modal */}
      {showMessaging && messagingRecipient && (
        <RealTimeChat
          recipientId={messagingRecipient.id}
          recipientName={messagingRecipient.name}
          onClose={() => {
            setShowMessaging(false);
            setMessagingRecipient(null);
          }}
        />
      )}
    </div>
  );
}
